import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO, money, FREQ_LABEL } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  User, CreditCard, Package, Settings as SettingsIcon, LifeBuoy, LogOut, Terminal, Lock,
  ExternalLink, Plus, ShieldCheck, Mail, Phone, Building2, Database,
} from "lucide-react";
import SystemDataViewer from "@/portal/SystemDataViewer";

const NAV = [
  { id: "account", label: "My Account", icon: User },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "services", label: "Services", icon: Package },
  { id: "data", label: "System Data", icon: Database },
  { id: "settings", label: "Settings", icon: SettingsIcon },
  { id: "support", label: "Support", icon: LifeBuoy },
];

function Pill({ status }) {
  const ok = ["active", "paid", "completed", "resolved"].includes(status);
  const warn = ["pending", "unpaid", "open", "initiated"].includes(status);
  const cls = ok ? "text-[#67F2B3] border-[#67F2B3]/30 bg-[#67F2B3]/10"
    : warn ? "text-amber-300 border-amber-400/30 bg-amber-400/10"
    : "text-[#F4FFF9]/50 border-white/10 bg-white/5";
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border capitalize ${cls}`}>{(status || "—").replace("_", " ")}</span>;
}

const CODE_LINES = [
  "class ServiceNode:", "    def __init__(self, sid, status):",
  "        self.sid = sid", "        self.status = status",
  "        self.linked = []", "", "def sync_core(nodes):",
  "    for node in nodes:", "        node.heartbeat()", "        node.reconcile()",
  "    return CoreState.OK", "", "core = Infrastructure(region='global')",
  "core.attach(services)", "core.verify_integrity()", "await core.stream(events)",
  "# edge mesh online", "status: nominal  uptime: stable",
];

function SystemCore({ service }) {
  const access = !!service.system_core_access;
  return (
    <div className="mt-4 rounded-xl border border-[#67F2B3]/20 bg-[#030B08] overflow-hidden" data-testid={`system-core-${service.service_id}`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#67F2B3]/15 bg-[#071A13]">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-[#67F2B3]" />
          <span className="font-mono text-xs tracking-widest text-[#67F2B3]">SYSTEM CORE</span>
        </div>
        <span className="font-mono text-[10px] text-[#F4FFF9]/40">Technical Interface Preview · Visualization Only</span>
      </div>
      <div className="relative h-40 overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-30"
          style={{ background: "radial-gradient(circle at 20% 30%, rgba(103,242,179,0.25), transparent 50%)" }} />
        <div className="animate-code-scroll px-4 py-3 font-mono text-[11px] leading-5 text-[#67F2B3]/80">
          {[...CODE_LINES, ...CODE_LINES].map((l, i) => (
            <div key={i} className="whitespace-pre">{l || "\u00A0"}</div>
          ))}
        </div>
        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-[#030B08] to-transparent" />
      </div>
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#67F2B3]/15 bg-[#071A13]">
        <span className="font-mono text-[10px] text-[#F4FFF9]/50">
          root@ecorexs:~$ <span className="animate-cursor">▋</span>
        </span>
        {access ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#67F2B3]">
            <ShieldCheck className="h-3 w-3" /> Access Granted
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#F4FFF9]/40 cursor-not-allowed select-none" title="Restricted">
            <Lock className="h-3 w-3" /> Restricted Access
          </span>
        )}
      </div>
    </div>
  );
}

function Panel({ children, className = "", ...rest }) {
  return <div className={`bg-white/[0.035] border border-[#67F2B3]/12 rounded-2xl p-5 ${className}`} {...rest}>{children}</div>;
}

function FilesPanel() {
  const [files, setFiles] = useState([]);
  const [termsCtx, setTermsCtx] = useState(null);
  const [terms, setTerms] = useState(null);
  const [lang, setLang] = useState("en");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/portal/files").then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const request = (id) => api.post(`/portal/files/${id}/request-download`, { reason: "" })
    .then(() => { toast.success("Download requested. We'll review and respond."); load(); })
    .catch((e) => toast.error(e.response?.data?.detail || "Could not request"));
  const openTerms = async (r, f) => {
    setAgreed(false); setLang("en");
    try { const t = await api.get("/billing/download-terms"); setTerms(t.data); setTermsCtx({ r, f }); }
    catch { toast.error("Terms are not available yet"); }
  };
  const acceptTerms = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/download-requests/${termsCtx.r.request_id}/accept-terms`, { version: terms.version });
      toast.success("Terms accepted"); setTermsCtx(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record acceptance"); }
    finally { setBusy(false); }
  };
  const pay = async (rid) => {
    try {
      const res = await api.post(`/portal/download-requests/${rid}/checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); }
  };
  const download = async (token) => {
    try {
      const res = await api.get(`/download/${token}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : "download";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Download delivered"); load();
    } catch (e) { toast.error(e.response?.status === 410 ? "Link expired — request a new authorization" : "Download failed"); load(); }
  };

  const link = "text-[#67F2B3] underline text-xs font-medium";
  const tag = (txt, cls) => <span className={`text-[11px] ${cls}`}>{txt}</span>;
  const reqBtn = (id, label) => <button onClick={() => request(id)} data-testid={`request-dl-${id}`} className={link}>{label}</button>;

  const renderAction = (f) => {
    const r = f.request;
    if (!r) return reqBtn(f.file_id, "Request Download");
    const expired = r.status === "ready" && r.token_expires && new Date(r.token_expires) < new Date();
    switch (r.status) {
      case "requested": return tag("Awaiting Approval", "text-amber-300");
      case "rejected": return <span className="flex items-center gap-2">{tag("Request Rejected", "text-red-300")}{reqBtn(f.file_id, "Try again")}</span>;
      case "approved":
        if (r.requires_terms && !r.terms_accepted) return <button onClick={() => openTerms(r, f)} data-testid={`review-terms-${f.file_id}`} className={link}>Review &amp; Accept Terms</button>;
        if (r.price > 0 && !r.paid) return <button onClick={() => pay(r.request_id)} data-testid={`pay-dl-${f.file_id}`} className={link}>Complete Secure Payment · {money(r.price)}</button>;
        return tag("Preparing…", "text-[#F4FFF9]/50");
      case "payment_pending": return <span className="flex items-center gap-2">{tag("Payment processing", "text-amber-300")}<button onClick={() => pay(r.request_id)} data-testid={`pay-dl-${f.file_id}`} className={link}>Retry payment</button></span>;
      case "ready":
        if (expired) return reqBtn(f.file_id, "Request New Authorization");
        return <button onClick={() => download(r.download_token)} data-testid={`download-${f.file_id}`} className={link}>Download Authorized File</button>;
      case "downloaded": return <span className="flex items-center gap-2">{tag("Download Delivered", "text-[#67F2B3]")}{reqBtn(f.file_id, "Request again")}</span>;
      case "expired": return reqBtn(f.file_id, "Request New Authorization");
      case "revoked": return <span className="flex items-center gap-2">{tag("Authorization Revoked", "text-red-300")}{reqBtn(f.file_id, "Request again")}</span>;
      default: return tag(r.status, "text-[#F4FFF9]/50");
    }
  };

  if (files.length === 0) return null;
  return (
    <Panel data-testid="portal-files">
      <div className="font-heading font-semibold mb-3">Available Files</div>
      <ul className="space-y-2">
        {files.map((f) => (
          <li key={f.file_id} className="flex items-center justify-between gap-3 text-sm border border-white/5 rounded-lg px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-[#F4FFF9]/85 truncate">{f.name}{f.version ? ` v${f.version}` : ""}<span className="text-[#F4FFF9]/50"> · {f.price > 0 ? money(f.price) : "free"}</span></div>
              {f.description && <div className="text-[11px] text-[#F4FFF9]/45 truncate">{f.description}</div>}
              {f.integrity_verified && (
                <div className="flex items-center gap-1 text-[10px] text-[#67F2B3]/80 mt-0.5" data-testid={`sha-verified-${f.file_id}`}>
                  <ShieldCheck className="h-3 w-3" /> SHA-256 Verified — Integrity Checked
                </div>
              )}
            </div>
            <span className="shrink-0">{renderAction(f)}</span>
          </li>
        ))}
      </ul>

      <Dialog open={!!termsCtx} onOpenChange={(o) => !o && setTermsCtx(null)}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto bg-[#071A13] border-[#67F2B3]/20 text-[#F4FFF9]" data-testid="terms-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4FFF9] flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#67F2B3]" /> Transfer Terms &amp; Conditions</DialogTitle>
            <DialogDescription className="text-[#F4FFF9]/55">
              {termsCtx?.f?.name}{termsCtx?.f?.version ? ` v${termsCtx.f.version}` : ""}
              {termsCtx?.r?.price > 0 ? ` · release fee ${money(termsCtx.r.price)}` : " · no release fee"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => setLang("en")} data-testid="terms-lang-en" className={`text-xs px-2 py-1 rounded ${lang === "en" ? "bg-[#67F2B3] text-[#071A13]" : "bg-white/5 text-[#F4FFF9]/60"}`}>English</button>
            <button onClick={() => setLang("es")} data-testid="terms-lang-es" className={`text-xs px-2 py-1 rounded ${lang === "es" ? "bg-[#67F2B3] text-[#071A13]" : "bg-white/5 text-[#F4FFF9]/60"}`}>Español</button>
            {terms?.version && <span className="ml-auto text-[10px] font-mono text-[#F4FFF9]/40">v{terms.version}</span>}
          </div>
          <div className="text-xs leading-5 text-[#F4FFF9]/75 bg-black/30 border border-white/10 rounded-lg p-3 max-h-56 overflow-y-auto" data-testid="terms-text">
            {lang === "en" ? terms?.text_en : terms?.text_es}
          </div>
          {terms?.requires_legal_review && <p className="text-[10px] text-amber-300/80 mt-2">Draft terms pending legal review.</p>}
          <label className="flex items-start gap-2 text-xs text-[#F4FFF9]/80 mt-3">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="terms-agree-checkbox" className="mt-0.5" />
            I have read and accept these transfer terms and conditions.
          </label>
          <div className="flex gap-2 mt-4">
            <Button onClick={acceptTerms} disabled={!agreed || busy || !terms} data-testid="terms-accept-btn" className="flex-1 bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg disabled:opacity-40">Accept &amp; Continue</Button>
            <Button onClick={() => setTermsCtx(null)} variant="outline" data-testid="terms-cancel-btn" className="border-white/15 text-[#F4FFF9] rounded-lg bg-transparent hover:bg-white/5">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

export default function PortalDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState("account");
  const [supportOpen, setSupportOpen] = useState(false);
  const [ticket, setTicket] = useState({ subject: "", message: "", service_id: null });

  const load = () => api.get("/portal/overview").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  const loadTickets = () => api.get("/portal/tickets").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); loadTickets(); }, []);

  const openBilling = async () => {
    try {
      const r = await api.post("/portal/billing-session", { return_url: window.location.href });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Billing portal unavailable"); }
  };

  const requestTerminate = async (service_id) => {
    if (!window.confirm("Request to end this service? Automatic renewal stops; you keep access through your paid period.")) return;
    try {
      await api.post(`/portal/services/${service_id}/terminate`, { reason: "", request_export: false });
      toast.success("Termination requested. Our team will follow up.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit request"); }
  };

  const submitSupport = async () => {
    if (!ticket.subject || !ticket.message) { toast.error("Subject and message required"); return; }
    try {
      await api.post("/portal/support", ticket);
      toast.success("Support request submitted");
      setSupportOpen(false);
      setTicket({ subject: "", message: "", service_id: null });
      loadTickets();
    } catch (e) { toast.error("Could not submit"); }
  };

  const bg = { background: "radial-gradient(130% 90% at 50% 0%, #0D3B2D 0%, #071A13 45%, #030B08 100%)" };

  if (data?.error) {
    return (
      <div className="min-h-screen flex flex-col text-[#F4FFF9]" style={bg}>
        <div className="p-6 flex items-center justify-between">
          <img src={LOGO.icon} alt="ECOREXS" className="h-9" />
          <button onClick={logout} className="text-sm text-[#F4FFF9]/60 flex items-center gap-1"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <div className="flex-1 flex items-center justify-center px-6">
          <Panel className="text-center max-w-md">
            <h1 className="font-heading font-bold text-xl">No account linked</h1>
            <p className="text-sm text-[#F4FFF9]/60 mt-2">This login ({user.email}) isn't linked to a client account yet. Please contact ECOREXS support.</p>
          </Panel>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen" style={bg} />;

  const c = data.client;

  return (
    <div className="min-h-screen text-[#F4FFF9]" style={bg}>
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#071A13]/70 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={LOGO.icon} alt="ECOREXS" className="h-8 w-auto object-contain" />
            <img src={LOGO.wordmark} alt="ECOREXS" className="h-5 w-auto object-contain hidden sm:block" />
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-[#F4FFF9]/60">{c.name}</span>
            <button onClick={logout} data-testid="portal-logout" className="text-sm text-[#F4FFF9]/60 hover:text-[#67F2B3] flex items-center gap-1">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-6 md:flex md:gap-6">
        {/* Desktop sidebar */}
        <aside className="hidden md:block w-52 shrink-0">
          <nav className="sticky top-20 space-y-1">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => setTab(n.id)} data-testid={`portal-nav-${n.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  tab === n.id ? "bg-[#67F2B3] text-[#071A13]" : "text-[#F4FFF9]/70 hover:bg-white/5"
                }`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 pb-24 md:pb-6">
          <h1 className="font-heading font-bold text-2xl mb-1" data-testid="portal-section-title">{NAV.find((n) => n.id === tab)?.label}</h1>
          <p className="text-sm text-[#F4FFF9]/50 mb-5">Welcome back, {c.name}.</p>

          {tab === "account" && (
            <div className="space-y-4" data-testid="portal-account">
              <Panel>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[11px] uppercase tracking-wide text-[#F4FFF9]/40">Account status</span>
                  <Pill status={c.account_status || "active"} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Info icon={User} label="Name" value={c.name} />
                  <Info icon={Mail} label="Email" value={c.email} />
                  {c.phone && <Info icon={Phone} label="Phone" value={c.phone} />}
                  {c.company && <Info icon={Building2} label="Company" value={c.company} />}
                </div>
              </Panel>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Services" value={data.services.length} />
                <Stat label="Subscriptions" value={data.subscriptions.length} />
                <Stat label="Payments" value={data.payments.length} />
              </div>
            </div>
          )}

          {tab === "billing" && (
            <div className="space-y-4" data-testid="portal-billing">
              <Panel>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-heading font-semibold">Payment methods & invoices</div>
                    <p className="text-sm text-[#F4FFF9]/55 mt-1">Manage cards, download invoices and cancel subscriptions securely via Stripe.</p>
                  </div>
                  <Button onClick={openBilling} data-testid="manage-billing-button" className="bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold rounded-lg">
                    <CreditCard className="h-4 w-4 mr-1" /> Manage Billing
                  </Button>
                </div>
              </Panel>
              <Panel>
                <div className="font-heading font-semibold mb-3">Subscriptions</div>
                {data.subscriptions.length === 0 ? <p className="text-sm text-[#F4FFF9]/50">No active subscriptions.</p> : data.subscriptions.map((s) => (
                  <div key={s.service_id} className="py-2.5 border-b border-white/5 last:border-0">
                    <Row title={s.name}
                      sub={`${FREQ_LABEL[s.frequency]}${s.billing_plan === "yearly" ? " plan · 20% annual discount" : s.billing_plan === "monthly" ? " plan" : ""}${s.next_renewal_date && !s.cancellation_scheduled ? ` · next renewal ${new Date(s.next_renewal_date).toLocaleDateString()}` : ""}`}
                      amount={`${money(s.amount)}${s.frequency === "yearly" ? "/yr" : s.frequency === "monthly" ? "/mo" : ""}`} status={s.subscription_status || "pending"} />
                    {s.cancellation_scheduled ? (
                      <div data-testid={`portal-cancel-${s.service_id}`} className="mt-1.5 text-[11px] text-amber-200/90">
                        Scheduled to end{s.service_end_date ? ` on ${new Date(s.service_end_date).toLocaleDateString()}` : ""} · automatic renewal off · access continues through your paid period.
                      </div>
                    ) : s.subscription_status === "active" ? (
                      <button onClick={() => requestTerminate(s.service_id)} data-testid={`portal-terminate-${s.service_id}`}
                        className="mt-1.5 text-[11px] text-amber-300/90 underline hover:no-underline">Request to end this service (keeps access through paid period)</button>
                    ) : null}
                  </div>
                ))}
              </Panel>
              <Panel>
                <div className="font-heading font-semibold mb-3">Payment history</div>
                {data.payments.length === 0 ? <p className="text-sm text-[#F4FFF9]/50">No payments yet.</p> : data.payments.map((p, i) => (
                  <Row key={i} title={p.service_name} sub={new Date(p.created_at).toLocaleDateString()} amount={money(p.amount)} status={p.payment_status} />
                ))}
              </Panel>
            </div>
          )}

          {tab === "services" && (
            <div className="space-y-4" data-testid="portal-services">
              <FilesPanel />
              {data.services.length === 0 ? <Panel><p className="text-sm text-[#F4FFF9]/50">No services assigned yet.</p></Panel> : data.services.map((s) => (
                <Panel key={s.service_id}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-heading font-semibold text-lg">{s.name}</div>
                      {s.description && <p className="text-sm text-[#F4FFF9]/55 mt-1">{s.description}</p>}
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[#67F2B3] font-semibold">{money(s.amount)}</div>
                      <div className="text-[11px] text-[#F4FFF9]/50">{s.payment_type === "subscription" ? FREQ_LABEL[s.frequency] : "one-time"}</div>
                      <div className="mt-1"><Pill status={s.payment_type === "subscription" ? (s.subscription_status || s.status) : s.payment_status} /></div>
                    </div>
                  </div>
                  {s.payment_type === "subscription" && (s.next_renewal_date || s.paid_through_date) && (
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[11px] text-[#F4FFF9]/50" data-testid={`portal-service-dates-${s.service_id}`}>
                      {s.next_renewal_date && !s.cancellation_scheduled && <span>Next renewal: {new Date(s.next_renewal_date).toLocaleDateString()}</span>}
                      {s.paid_through_date && <span>Paid through: {new Date(s.paid_through_date).toLocaleDateString()}</span>}
                      {s.contract_end_date && <span>Contract ends: {new Date(s.contract_end_date).toLocaleDateString()}</span>}
                    </div>
                  )}
                  {s.cancellation_scheduled && (
                    <div data-testid={`portal-cancel-${s.service_id}`}
                      className="mt-3 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-400/25 rounded-lg px-3 py-2">
                      Your subscription is scheduled to end on {s.service_end_date ? new Date(s.service_end_date).toLocaleDateString() : "the end of your billing period"}. Your service remains available through the end of your current paid billing period. Automatic renewal is off.
                    </div>
                  )}
                  <SystemCore service={s} />
                </Panel>
              ))}
            </div>
          )}

          {tab === "data" && <SystemDataViewer />}

          {tab === "settings" && (
            <div className="space-y-4" data-testid="portal-settings">
              <Panel>
                <div className="font-heading font-semibold mb-3">Profile</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Info icon={User} label="Name" value={c.name} />
                  <Info icon={Mail} label="Email" value={c.email} />
                </div>
                <p className="text-xs text-[#F4FFF9]/40 mt-4">To update your details, contact ECOREXS support.</p>
              </Panel>
              <Panel>
                <div className="font-heading font-semibold mb-2">Security</div>
                <p className="text-sm text-[#F4FFF9]/55">Your account is protected by a private password and an encrypted session. Billing is handled by Stripe — ECOREXS never stores your card details.</p>
                <Button onClick={logout} variant="outline" className="mt-4 border-[#67F2B3]/25 text-[#F4FFF9] hover:bg-white/5 rounded-lg" data-testid="settings-signout">
                  <LogOut className="h-4 w-4 mr-1" /> Sign out
                </Button>
              </Panel>
            </div>
          )}

          {tab === "support" && (
            <div className="space-y-4" data-testid="portal-support">
              <Panel>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-heading font-semibold">Need a hand?</div>
                    <p className="text-sm text-[#F4FFF9]/55 mt-1">Send us a message about your account or a specific service.</p>
                  </div>
                  <Button onClick={() => setSupportOpen(true)} data-testid="portal-support-button" className="bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold rounded-lg">
                    <Plus className="h-4 w-4 mr-1" /> New Request
                  </Button>
                </div>
              </Panel>
              <Panel>
                <div className="font-heading font-semibold mb-3">Ticket history</div>
                {tickets.length === 0 ? <p className="text-sm text-[#F4FFF9]/50">No tickets yet.</p> : tickets.map((t) => (
                  <div key={t.ticket_id} className="py-3 border-b border-white/5 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{t.subject}</span><Pill status={t.status} />
                    </div>
                    <p className="text-sm text-[#F4FFF9]/55 mt-1">{t.message}</p>
                    {t.replies?.map((r, i) => (
                      <p key={i} className="text-sm text-[#67F2B3]/90 mt-2 bg-[#67F2B3]/5 border border-[#67F2B3]/15 rounded-lg p-2.5">
                        <span className="font-mono text-[11px]">{r.author}:</span> {r.message}
                      </p>
                    ))}
                  </div>
                ))}
              </Panel>
            </div>
          )}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-[#071A13]/95 backdrop-blur border-t border-white/10 z-40">
        <div className="grid grid-cols-6">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`m-portal-nav-${n.id}`}
              className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${tab === n.id ? "text-[#67F2B3]" : "text-[#F4FFF9]/50"}`}>
              <n.icon className="h-5 w-5" /> {n.label.replace("My ", "").replace("System ", "")}
            </button>
          ))}
        </div>
      </nav>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="sm:max-w-md" data-testid="portal-support-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0D3B2D]">Contact Support</DialogTitle>
            <DialogDescription className="text-[#4A635A]">Send us a message about your account or a service.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Subject</Label><Input value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} data-testid="support-subject-input" className="bg-white border-[#E2EBF0]" /></div>
            <div>
              <Label>Related Service</Label>
              <select value={ticket.service_id || ""} onChange={(e) => setTicket({ ...ticket, service_id: e.target.value || null })}
                data-testid="support-service-select" className="w-full mt-1 bg-white border border-[#E2EBF0] rounded-lg px-3 py-2 text-sm">
                <option value="">General</option>
                {data.services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
              </select>
            </div>
            <div><Label>Message</Label><Textarea value={ticket.message} onChange={(e) => setTicket({ ...ticket, message: e.target.value })} rows={4} data-testid="support-message-input" className="bg-white border-[#E2EBF0]" /></div>
            <Button onClick={submitSupport} data-testid="support-submit-button" className="w-full bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg">Send Request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-[#67F2B3]/10 border border-[#67F2B3]/20 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#67F2B3]" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-[#F4FFF9]/40">{label}</div>
        <div className="text-sm text-[#F4FFF9] truncate">{value}</div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/[0.035] border border-[#67F2B3]/12 rounded-xl p-4 text-center">
      <div className="font-mono font-bold text-2xl text-[#67F2B3]">{value}</div>
      <div className="text-[11px] text-[#F4FFF9]/50 mt-1">{label}</div>
    </div>
  );
}

function Row({ title, sub, amount, status }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
      <div className="min-w-0">
        <div className="text-sm text-[#F4FFF9] truncate">{title}</div>
        <div className="text-[11px] text-[#F4FFF9]/45">{sub}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="font-mono text-sm text-[#F4FFF9]">{amount}</span>
        <Pill status={status} />
      </div>
    </div>
  );
}
